import { useAsync } from '@tiger/hooks';
import { cloneDeep } from 'lodash';
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ErrorBox from 'components/ErrorBox';
import HashLink from 'components/HashLink';
import Icon from 'components/Icon';
import Loading from 'components/Loading';
import Toast from 'components/Toast';
import Track from 'components/Track';
import { getStep } from 'modules/Security/components/Steps';
import store from 'modules/Security/modules/Modify/store';
import { SecurityActionType } from 'modules/Security/type';
import { ReactComponent as ArrowRightIcon } from 'static/images/icons/arrow-right.svg';
import require2FA from 'utils/require2FA';
import { eventType, processType } from '../../../helper';
import emailImg from './images/email.png';
import otpImg from './images/otp.png';
import smsImg from './images/sms.png';
import './style.scss';

const methods = {
    otp: {
        title: __('TradeUP Authenticator'),
        link: '',
        img: otpImg,
        show: true,
        eventName: 'TradeUP Authenticator'
    },
    phone: {
        title: __('短信验证'),
        link: 'msg',
        img: smsImg,
        show: true,
        eventName: '短信验证'
    },
    email: {
        title: __('邮箱验证'),
        link: 'mail',
        img: emailImg,
        show: true,
        eventName: '邮箱验证'
    }
};

/** 选择验证方式: 令牌，短信、邮箱 */
const ConfirmOptions: React.FC<{ type: SecurityActionType }> = ({ type }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [state, getData] = useAsync(() => {
        const params =
            type === 'upgradePassword'
                ? {
                      process: 'update_login_password' // 业务场景，目前仅支持更新密码update_login_password
                  }
                : {};

        return store.getVerifyOption(params).then(res => {
            const curMethods = cloneDeep(methods);

            Object.keys(res.data.options).forEach(item => {
                curMethods[item].show = res.data.options[item];
            });

            return curMethods;
        });
    });

    // TradeUP Authenticator验证
    const otpVerify = () => {
        require2FA()
            .then(code => {
                onSubmit(code);
            })
            .catch(e => {
                Toast.show(e.message);
            });
    };

    // 验证令牌
    const onSubmit = async otp_code => {
        try {
            Toast.loading(true);

            const resp = await store.verifyOtpCode({
                process: processType[type],
                otp_code
            });

            // 需要进行下一步，进行证件号码验证
            if (resp.data.next_step?.includes('id_no')) {
                navigate('id');
            } else {
                const currentStep = getStep(location.pathname);

                // 跳转下一步时step：修改密码时为3，更新密码时为2
                navigate(`../${currentStep + 1}`, {
                    state: {
                        verify_token: resp.data.verify_token
                    }
                });
            }
        } catch (error) {
            Toast.show(error);
        }

        Toast.loading(false);
    };

    const renderItem = (item, index) => {
        if (item.show) {
            if (item.link) {
                return (
                    <Track eventModel="安全中心" eventType={eventType[type]} eventName={item.eventName} key={index}>
                        <Link
                            to={item.link}
                            state={location.state}
                            key={index}
                            className="menu-item"
                            title={item.title}>
                            <img src={item.img} alt={item.title} />
                            <div className="item-title">{item.title}</div>
                            <Icon title={item.eventName} className="icon-arrow-right" component={ArrowRightIcon} />
                        </Link>
                    </Track>
                );
            }

            return (
                <Track eventModel="安全中心" eventType={eventType[type]} eventName="修改_验证码" key={index}>
                    <HashLink className="menu-item" onClick={otpVerify} title={item.title}>
                        <img src={item.img} alt={item.title} />
                        <div className="item-title">{item.title}</div>
                        <Icon title={item.eventName} className="icon-arrow-right" component={ArrowRightIcon} />
                    </HashLink>
                </Track>
            );
        }

        return null;
    };

    return (
        <div className="modify-phone-confirm-options">
            {state.loading ? (
                <Loading tip={__('请稍等...')} />
            ) : state.error ? (
                <ErrorBox error={state.error} onClick={getData} />
            ) : (
                <>
                    <p className="tips">
                        {__('To protect your account, please verify your identity using one of the following methods.')}
                    </p>
                    {Object.values(state.data!).map((item, index) => renderItem(item, index))}
                </>
            )}
        </div>
    );
};

export default ConfirmOptions;
